/**
 * Payment Flow Event Tracking
 * Tracks user journey through payment process
 */

import { posthog } from '../posthog';
import type {
  PaymentInitiatedProperties,
  PaymentWidgetLoadedProperties,
  PaymentCompletedProperties,
  PaymentFailedProperties,
  PaymentRetryAttemptedProperties,
  RefundInitiatedProperties,
} from '../types';

// Store payment initiation times for calculating duration
const paymentStartTimes = new Map<string, number>();

export const paymentEvents = {
  /**
   * Track when payment is initiated
   * Trigger: POST to /api/payment/create called
   */
  initiated: (props: Omit<PaymentInitiatedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    paymentStartTimes.set(props.content_id, timestamp);

    posthog.capture('payment_initiated', {
      ...props,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track when payment widget is loaded and interactive
   * Trigger: Widget becomes interactive
   */
  widgetLoaded: (props: Omit<PaymentWidgetLoadedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const startTime = paymentStartTimes.get(props.content_id);
    const loadTime = startTime ? timestamp - startTime : props.load_time_ms;

    posthog.capture('payment_widget_loaded', {
      ...props,
      load_time_ms: loadTime,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track successful payment completion
   * Trigger: Successful payment callback received
   */
  completed: (props: Omit<PaymentCompletedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const startTime = paymentStartTimes.get(props.content_id);
    const processingTime = startTime ? timestamp - startTime : props.processing_time_ms;

    // Clean up stored start time
    paymentStartTimes.delete(props.content_id);

    posthog.capture('payment_completed', {
      ...props,
      processing_time_ms: processingTime,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track payment failure
   * Trigger: Payment declined or cancelled
   */
  failed: (props: Omit<PaymentFailedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('payment_failed', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track payment retry attempts
   * Trigger: User retries after failed payment
   */
  retryAttempted: (props: Omit<PaymentRetryAttemptedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    // Reset start time for retry
    paymentStartTimes.set(props.content_id, Date.now());

    posthog.capture('payment_retry_attempted', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track refund initiation
   * Trigger: Organizer initiates refund from CRM
   */
  refundInitiated: (props: Omit<RefundInitiatedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('refund_initiated', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Clear stored payment start time
   */
  clearStartTime: (contentId: string) => {
    paymentStartTimes.delete(contentId);
  },

  /**
   * Get time elapsed since payment initiated
   */
  getTimeElapsed: (contentId: string): number | undefined => {
    const startTime = paymentStartTimes.get(contentId);
    return startTime ? Date.now() - startTime : undefined;
  },
};
